// src/admin/pages/Vehicles.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import { Card } from "../ui/Card";
import { Badge } from "../ui/Badge";
import { Table } from "../ui/Table";

function normalize(s) {
  return String(s || "").toLowerCase().trim();
}

function prettyCategory(cat) {
  const c = String(cat || "").toLowerCase();
  if (c === "travel_trailer") return "Travel Trailer";
  if (c === "atv") return "ATV";
  if (c === "sxs") return "Side-by-Side";
  if (c === "boat") return "Boat";
  if (c === "watercraft") return "Watercraft";
  if (c === "snowmobile") return "Snowmobile";
  if (c === "golf_cart") return "Golf Cart";
  return cat || "—";
}

function toneForVehicleStatus(status) {
  const s = String(status || "").toLowerCase();
  if (s === "available") return "success";
  if (s === "booked") return "info";
  if (s === "cleaning") return "warning";
  if (s === "maintenance") return "danger";
  if (s === "transit") return "neutral";
  if (s === "ops_hold") return "neutral";
  return "neutral";
}

export default function Vehicles() {
  const nav = useNavigate();

  const [category, setCategory] = useState("All");
  const [status, setStatus] = useState("All");
  const [q, setQ] = useState("");

  const [vehicles, setVehicles] = useState([]);
  const [approvedActive, setApprovedActive] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState(null);

  // Load vehicles + recent approved/active bookings for "available now"
  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setLoadErr(null);

      try {
        const [
          { data: vData, error: vErr },
          { data: bData, error: bErr },
        ] = await Promise.all([
          supabase
            .from("vehicles")
            .select(
              "id, name, category, sub_category, make, model, year, trim, status, location, tier_access, card_tag, card_desc, created_at"
            )
            .order("created_at", { ascending: false })
            .limit(1000),
          supabase
            .from("bookings")
            .select("id, vehicle_id, start_date, end_date, status")
            .in("status", ["approved", "active"])
            .order("created_at", { ascending: false })
            .limit(1500),
        ]);

        if (vErr) throw vErr;
        if (bErr) throw bErr;

        if (!cancelled) {
          setVehicles(vData || []);
          setApprovedActive(bData || []);
        }
      } catch (e) {
        if (!cancelled) setLoadErr(e.message || "Failed to load vehicles.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const categories = useMemo(() => {
    const unique = Array.from(new Set((vehicles || []).map((v) => prettyCategory(v.category))));
    return ["All", ...unique.sort()];
  }, [vehicles]);

  const rows = useMemo(() => {
    const todayISO = new Date().toISOString().slice(0, 10);
    const todayTime = new Date(todayISO).getTime();

    function isBookedToday(vehicleId) {
      return (approvedActive || []).some((b) => {
        if (b.vehicle_id !== vehicleId) return false;
        const s = new Date(b.start_date).getTime();
        const e = new Date(b.end_date).getTime();
        // inclusive overlap with today
        return s <= todayTime && todayTime <= e;
      });
    }

    const wantCat = normalize(category);
    const wantStatus = normalize(status);
    const query = normalize(q);

    return (vehicles || [])
      .filter((v) => {
        if (category === "All") return true;
        return normalize(prettyCategory(v.category)) === wantCat;
      })
      .filter((v) => {
        if (status === "All") return true;
        return normalize(v.status) === wantStatus;
      })
      .filter((v) => {
        if (!query) return true;
        const hay = normalize(
          `${v.name || ""} ${v.make || ""} ${v.model || ""} ${v.trim || ""} ${v.year || ""} ${v.location || ""} ${
            v.sub_category || ""
          } ${(v.tier_access || []).join(" ")}`
        );
        return hay.includes(query);
      })
      .map((v) => {
        const bookedToday = isBookedToday(v.id);

        // "Available now" = ops status is available AND not booked today by approved/active booking
        const opsAvailable = normalize(v.status) === "available";
        const availNow = opsAvailable && !bookedToday;

        return {
          __key: v.id,
          ...v,
          categoryLabel: prettyCategory(v.category),
          availNow,
          availReason: !opsAvailable
            ? `Ops status: ${v.status}`
            : bookedToday
            ? "Has an approved/active booking today"
            : "Clear",
        };
      });
  }, [vehicles, approvedActive, category, status, q]);

  const columns = [
    {
      key: "name",
      header: "Vehicle",
      width: "36%",
      cell: (r) => (
        <div className="cellTitle">
          <div className="cellTitle__top">{r.name}</div>
          <div className="cellTitle__sub">
            {r.categoryLabel}
            {r.sub_category ? ` • ${r.sub_category}` : ""}
            {r.year ? ` • ${r.year}` : ""}
          </div>
        </div>
      ),
    },
    {
      key: "status",
      header: "Ops Status",
      width: "14%",
      cell: (r) => <Badge tone={toneForVehicleStatus(r.status)}>{r.status}</Badge>,
    },
    {
      key: "avail",
      header: "Available now",
      width: "14%",
      cell: (r) =>
        r.availNow ? (
          <Badge tone="success">Yes</Badge>
        ) : (
          <div>
            <Badge tone="danger">No</Badge>
            <div className="muted" style={{ marginTop: 4, fontSize: 12 }}>
              {r.availReason}
            </div>
          </div>
        ),
    },
    {
      key: "access",
      header: "Access",
      width: "20%",
      cell: (r) => (
        <span className="muted">
          {Array.isArray(r.tier_access) ? r.tier_access.join(", ") : "—"}
        </span>
      ),
    },
    {
      key: "location",
      header: "Location",
      width: "16%",
      cell: (r) => <span className="muted">{r.location || "—"}</span>,
    },
  ];

  return (
    <div className="page">
      <div className="page__head page__head--row">
        <div>
          <h1 className="page__title">Vehicles</h1>
          <div className="page__sub">Fleet list from Supabase + derived “available now”.</div>
        </div>

        <Link className="btn btn--primary" to="/admin/vehicles/new">
          + Add Vehicle
        </Link>
      </div>

      {loading && <div className="memMuted">Loading vehicles from Supabase…</div>}
      {loadErr && (
        <div className="adminAuth__error" style={{ marginTop: 10 }}>
          {loadErr}
        </div>
      )}

      <Card
        title="Fleet"
        right={
          <div className="filters" style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <input
              className="memInput"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, model, location…"
              style={{ width: 240 }}
              disabled={loading}
            />

            <select className="select" value={category} onChange={(e) => setCategory(e.target.value)} disabled={loading}>
              {categories.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>

            <select className="select" value={status} onChange={(e) => setStatus(e.target.value)} disabled={loading}>
              <option value="All">All</option>
              <option value="available">available</option>
              <option value="booked">booked</option>
              <option value="cleaning">cleaning</option>
              <option value="maintenance">maintenance</option>
              <option value="transit">transit</option>
              <option value="ops_hold">ops_hold</option>
            </select>
          </div>
        }
      >
        <Table
          columns={columns}
          rows={rows}
          onRowClick={(r) => nav(`/admin/vehicles/${r.id}`)}
        />
      </Card>
    </div>
  );
}